// «Evento privado» en una pantalla (cumpleaños, festejos con todo pago): lo que la pantalla
// decide sin pedirle nada a nadie, en funciones PURAS. Reciben el formulario como un objeto plano
// y devuelven qué mesas proponer, qué falta, qué viaja al servidor y qué se precarga de un evento
// anterior. La pantalla (`FormPrivateEventComponent`) sólo dibuja y llama.
//
// Lo que NO se decide acá, porque es del servidor: si el salón abre a esa hora y qué mesas están
// libres (la jornada la deriva él, no el navegador), si un ítem sigue en el menú, y todas las
// validaciones de `POST /manage-events/private`, que se repiten acá sólo para avisar antes de
// mandar y todas juntas.

import { TICKET_PAYMENT_METHOD } from './enums';
import { convertToMoney, formatEventDateTime } from './formats';
import { inclusionsError, inclusionsPayload, itemsNotInMenu, rowsFromInclusions } from '../components/EventInclusionsEditor';

// Los mismos topes que valida la ruta. 50 es el máximo de entradas por operación de venta: el
// alta vende las N juntas, así que más de 50 personas no entran en un evento privado.
export const LIMITS = {
    title: 200,
    people: 50,
};

// Medios de la venta presencial (espejo de PRESENCIAL_METHODS del backend), en el orden del
// modal «Venta presencial».
export const PAYMENT_METHODS = [
    { value: 'efectivo', label: TICKET_PAYMENT_METHOD[1] },
    { value: 'transferencia', label: TICKET_PAYMENT_METHOD[2] },
    { value: 'pos', label: TICKET_PAYMENT_METHOD[5] },
    { value: 'mercadopago', label: TICKET_PAYMENT_METHOD[3] },
];

// A qué bloque de la tarjeta pertenece cada campo que se valida, con el rótulo de pantalla: de
// acá sale el resumen de lo que falta que va al lado del botón (`missingBlocks`).
export const FIELD_BLOCK = {
    branchId: 'Sucursal',
    title: 'Nombre',
    startsAt: 'Fecha y hora',
    people: 'Personas',
    price: 'Precio por persona',
    inclusions: 'Qué incluye',
    tables: 'Mesas',
    customer: 'Quién reserva',
    cedula: 'Quién reserva',
    customerName: 'Quién reserva',
    customerLastName: 'Quién reserva',
    customerPhone: 'Quién reserva',
    customerEmail: 'Quién reserva',
};

const texto = (value) => String(value == null ? '' : value).trim();

// Un entero tipeado: el número, `null` si está vacío o `NaN` si no es un entero. Un punto o
// una coma como separador de miles («1.000», «12.500») se RECHAZA en vez de adivinar: `Number`
// lo leería como 1 o como 12,5, y ninguno es lo que se quiso escribir (misma regla que
// `normalizarImporte`).
const entero = (value) => {
    const raw = texto(value);
    if (raw === '') return null;
    if (/^\d{1,3}([.,]\d{3})+$/.test(raw)) return NaN;
    const n = Number(raw.replace(',', '.'));
    return Number.isInteger(n) ? n : NaN;
};

// Cuántas personas son, o null mientras no haya un entero de 1 para arriba.
export const peopleCount = (value) => {
    const n = entero(value);
    return n >= 1 ? n : null;
};

// «2026-10-20T20:00»: lo que entrega un <input type="datetime-local">. Se manda tal cual; qué
// jornada es la decide el servidor.
const STARTS_AT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
export const isStartsAt = (value) => STARTS_AT_RE.test(texto(value));

// ¿La jornada de esa fecha y hora ya pasó? Sale de dos fechas que da el SERVIDOR en la
// disponibilidad, sin derivar nada acá: `date` es la jornada del salón que le tocó a la hora
// consultada y `businessDate` la jornada en curso. Una jornada anterior a la de hoy es la que el
// alta rechaza («esa fecha ya pasó: no se le podrían vender las entradas»); decirlo acá evita
// llenar toda la tarjeta para enterarse al final. Lo que esto NO ve es la jornada de hoy después
// del cierre del salón: ahí rechaza el servidor. Sin alguna de las dos fechas no se opina.
export const jornadaPasada = (disponibilidad) => {
    const jornada = texto(disponibilidad && disponibilidad.date);
    const hoy = texto(disponibilidad && disponibilidad.businessDate);
    return jornada !== '' && hoy !== '' && jornada < hoy;
};

// ── Mesas ─────────────────────────────────────────────────────────────────────────────────

const lugaresDe = (table) => Number(table && table.capacity) || 0;

// Las mesas que vienen tildadas solas, de las LIBRES, hasta que los lugares alcancen para todos:
// primero las comunes, después las VIP y recién al final las que tienen otra reserva más tarde
// esa noche (`nextReservation`): se pueden tomar, pero el evento no tiene hora de fin y esa
// reserva se le viene encima. Dentro de cada grupo, de mayor a menor. Si para llegar hubo que
// sumar una mesa de un grupo peor, puede sobrar alguna de las primeras: se sacan las que no
// hacen falta, de la más chica a la más grande, sin dejar de cubrir. Si ni con todas alcanza,
// van todas (la pantalla avisa que quedan apretados, sin bloquear). Las ocupadas y las
// bloqueadas no se proponen nunca. Devuelve los ids en el orden de la lista.
export const proposeTables = (tables, people) => {
    const n = entero(people);
    if (!(n >= 1)) return [];
    const libres = (tables || []).filter((t) => t && t.state === 'free');
    const grupo = (t) => (t.nextReservation ? 2 : (t.isVip ? 1 : 0));
    const candidatas = [...libres].sort((a, b) => (grupo(a) - grupo(b)) || (lugaresDe(b) - lugaresDe(a)));
    const elegidas = new Set();
    let lugares = 0;
    for (const table of candidatas) {
        if (lugares >= n) break;
        elegidas.add(table);
        lugares += lugaresDe(table);
    }
    [...elegidas].sort((a, b) => lugaresDe(a) - lugaresDe(b)).forEach((table) => {
        if (lugares - lugaresDe(table) >= n) {
            elegidas.delete(table);
            lugares -= lugaresDe(table);
        }
    });
    return libres.filter((t) => elegidas.has(t)).map((t) => Number(t.id));
};

// De las mesas elegidas, las que siguen libres en la disponibilidad nueva (cambió la fecha o la
// hora y el usuario ya había tocado las casillas). `dropped` son las etiquetas de las que se
// cayeron, para decirlo en pantalla.
export const keepFreeTables = (tables, tableIds) => {
    const porId = new Map((tables || []).map((t) => [Number(t.id), t]));
    const kept = [];
    const dropped = [];
    (tableIds || []).forEach((id) => {
        const table = porId.get(Number(id));
        if (table && table.state === 'free') kept.push(Number(id));
        else dropped.push(table ? table.label : `Mesa #${id}`);
    });
    return { tableIds: kept, dropped };
};

// «20 personas · 24 lugares», y el aviso cuando los lugares no alcanzan. El aviso no bloquea:
// sentar 7, 7 y 6 en tres mesas de 6 es un uso que el salón admite.
export const tablesSummary = (tables, tableIds, people) => {
    const elegidas = new Set((tableIds || []).map(Number));
    const seats = (tables || []).filter((t) => elegidas.has(Number(t.id))).reduce((acc, t) => acc + lugaresDe(t), 0);
    const n = entero(people);
    const conPersonas = n >= 1;
    const lugares = `${seats} ${seats === 1 ? 'lugar' : 'lugares'}`;
    return {
        seats,
        text: conPersonas
            ? `${n} ${n === 1 ? 'persona' : 'personas'} · ${lugares}`
            : `${elegidas.size} ${elegidas.size === 1 ? 'mesa' : 'mesas'} · ${lugares}`,
        warning: conPersonas && elegidas.size > 0 && seats < n
            ? `Hay ${n} ${n === 1 ? 'persona' : 'personas'} y ${lugares}: van a quedar apretados`
            : null,
    };
};

// ── Qué incluye ───────────────────────────────────────────────────────────────────────────

// «quantity cada everyN personas», redondeado para arriba: la misma cuenta que hace el servidor
// (`allowance` en backend/src/utils/eventIncluded.js): 19 personas a una pizza cada dos son 10.
export const allowance = (people, quantity, everyN) => {
    const n = Number(everyN);
    if (!(n > 0)) return 0;
    return Math.max(0, Math.ceil((Number(people) || 0) * (Number(quantity) || 0) / n));
};

// La línea «Para 20 personas: 10 Pizza · 20 Bebida» mientras se arma el evento, con las mismas
// palabras que la tarjeta «Incluido en la entrada». Los renglones de sólo texto van aparte
// («También incluye»). Null si todavía no hay personas o no hay nada que contar.
export const includedPreview = (rows, people) => {
    const n = entero(people);
    if (!(n >= 1)) return null;
    const counted = [];
    const notes = [];
    (rows || []).forEach((row) => {
        const label = texto(row.label);
        if (label === '') return;
        if (row.matchKind === 'free') {
            if (!notes.includes(label)) notes.push(label);
            return;
        }
        const quantity = entero(row.quantity);
        const everyN = entero(row.everyNPeople);
        if (!(quantity >= 1) || !(everyN >= 1)) return;
        counted.push(`${allowance(n, quantity, everyN)} ${label}`);
    });
    if (counted.length === 0 && notes.length === 0) return null;
    return {
        title: `Para ${n} ${n === 1 ? 'persona' : 'personas'}`,
        counted: counted.join(' · '),
        notes: notes.join(' · '),
    };
};

// Los eventos de los que se puede copiar «Qué incluye»: los que declaran algo (`hasInclusions`
// de la lista admin), el más nuevo primero y con su fecha. El orden se arma comparando los
// dígitos de la fecha, sin pasar por Date.
export const copyableEvents = (events) => {
    const clave = (event) => String(event.starts_at || '').slice(0, 16).replace('T', ' ');
    return (events || [])
        .filter((event) => event && event.hasInclusions)
        .sort((a, b) => clave(b).localeCompare(clave(a)))
        .map((event) => ({ id: event.id, title: event.title, label: `${event.title} — ${formatEventDateTime(event.starts_at)}` }));
};

// Del detalle de un evento anterior a lo que se precarga: los renglones del editor y el precio.
// Sale del primer tipo de entrada que declara algo (los activos primero). Null si ninguno declara
// nada o si no se pudo leer (`inclusions: null`). Los renglones por ítems se llevan el nombre de
// cada ítem (`items` del detalle): si alguno salió del menú después, se lo nombra igual.
export const prefillFromEvent = (event) => {
    const types = (event && event.ticketTypes) || [];
    const conRenglones = types.filter((tt) => tt && Array.isArray(tt.inclusions) && tt.inclusions.length > 0);
    const activo = (tt) => tt.active !== false && Number(tt.active) !== 0;
    const elegido = conRenglones.find(activo) || conRenglones[0];
    if (!elegido) return null;
    return {
        rows: rowsFromInclusions(elegido.inclusions),
        price: elegido.price != null ? String(elegido.price) : '',
        ticketTypeName: elegido.name || '',
    };
};

// ── Quién reserva ─────────────────────────────────────────────────────────────────────────

// La cédula en dígitos pelados: «4.711.223-3» y «47112233» son la misma.
export const cedulaDigits = (value) => texto(value).replace(/\D/g, '');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Lo que se tipeó de quien reserva, limpio.
const customerOf = (form) => ({
    cedula: cedulaDigits(form.cedula),
    name: texto(form.customerName),
    lastName: texto(form.customerLastName),
    email: texto(form.customerEmail),
    phone: texto(form.customerPhone),
});

// ¿Se cargó algo de quien reserva? Con «Cobrar ahora» y los cinco campos vacíos la venta va a
// consumidor final (el «cliente sin datos» del modal de venta).
export const hasCustomerData = (form) => [form.cedula, form.customerName, form.customerLastName, form.customerEmail, form.customerPhone]
    .some((value) => texto(value) !== '');

// Lo que devuelve la búsqueda por cédula (`GET clients/:cedula`: `{ profile, account }` o null),
// aplicado al formulario con la regla del modal «Venta presencial»: si es la MISMA persona sólo
// se completan los campos vacíos (lo tipeado es el dato más fresco); si la cédula cambió, lo que
// había es de otra persona y se pisa. Devuelve el parche de estado.
export const applyClientLookup = (form, cedula, cliente) => {
    const otraPersona = !!form.datosCedula && form.datosCedula !== cedula;
    if (!cliente) {
        return {
            clientHint: { ok: false, text: 'Cliente nuevo' },
            datosCedula: cedula,
            ...(otraPersona ? { customerName: '', customerLastName: '', customerEmail: '', customerPhone: '' } : {}),
        };
    }
    const perfil = cliente.profile || {};
    const cuenta = cliente.account || {};
    const completar = (actual, ...candidatos) =>
        (texto(actual) && !otraPersona ? actual : (candidatos.find((v) => v && texto(v)) || ''));
    const nombre = completar(form.customerName, perfil.firstName, cuenta.firstName);
    const apellido = completar(form.customerLastName, perfil.lastName, cuenta.lastName);
    return {
        customerName: nombre,
        customerLastName: apellido,
        customerEmail: completar(form.customerEmail, perfil.email, cuenta.email),
        customerPhone: completar(form.customerPhone, perfil.phone, cuenta.phone),
        datosCedula: cedula,
        clientHint: { ok: true, text: `✓ ${`${nombre} ${apellido}`.trim() || 'Cliente encontrado'}` },
    };
};

// ── Validación ────────────────────────────────────────────────────────────────────────────

// Un renglón de «Qué incluye» que nadie tocó: sin etiqueta y sin nada elegido.
export const isBlankRow = (row) => texto(row && row.label) === '' && ((row && row.matchValue) || []).length === 0;

// El mismo motivo con el que rechaza el servidor una fecha cuya jornada ya cerró.
export const PAST_DATE_TEXT = 'Esa fecha ya pasó: no se le podrían vender las entradas.';

// TODO lo que falta, junto: una lista de `{ field, message }` en el orden de la tarjeta (vacía
// si se puede crear). `field` es una clave de FIELD_BLOCK; la pantalla pone cada mensaje al lado
// de su campo.
//
//   form   el formulario (title, startsAt, people, price, rows, tableIds, paymentMode,
//          paymentMethod, los cinco datos de quien reserva, branchId).
//   ctx    lo que la pantalla sabe del servidor:
//            availability   la disponibilidad de esa fecha y hora (`{ tables, open, closedReason,
//                           date, businessDate }`), o null si todavía no llegó;
//            loadingTables  se está pidiendo;
//            tablesError    el pedido falló (texto);
//            menu           el menú de cafetería, o null si no se pudo saber.
export const validatePrivateEvent = (form, ctx = {}) => {
    const out = [];
    const falta = (field, message) => out.push({ field, message });

    if (texto(form.branchId) === '') falta('branchId', 'Elegí la sucursal.');

    const title = texto(form.title);
    if (title === '') falta('title', 'Falta el nombre.');
    else if (title.length > LIMITS.title) falta('title', `El nombre admite hasta ${LIMITS.title} caracteres.`);

    const conFecha = isStartsAt(form.startsAt);
    const pasada = conFecha && !ctx.loadingTables && jornadaPasada(ctx.availability);
    if (texto(form.startsAt) === '') falta('startsAt', 'Falta la fecha y hora.');
    else if (!conFecha) falta('startsAt', 'La fecha y hora no se pudo leer.');
    else if (pasada) falta('startsAt', PAST_DATE_TEXT);

    const people = entero(form.people);
    if (people === null) falta('people', 'Falta cuántas personas son.');
    else if (!(people >= 1)) falta('people', `Las personas van en un número entero, de 1 a ${LIMITS.people}.`);
    else if (people > LIMITS.people) falta('people', `Hasta ${LIMITS.people} personas: es el tope de entradas por venta.`);

    const price = entero(form.price);
    if (price === null) falta('price', 'Falta el precio por persona.');
    else if (Number.isNaN(price)) falta('price', 'El precio va en pesos enteros, sin centavos ni separador de miles.');
    else if (price < 0) falta('price', 'El precio no puede ser negativo.');

    // Qué incluye: los mismos avisos del editor, más el que es de este tipo de evento — sin un
    // renglón contable no hay «Sentar a todos» ni reparto del pedido en el POS.
    // La tarjeta arranca con un renglón en blanco abierto: mientras sea lo único que hay, lo que
    // falta es cargar algo, no «el renglón 1 no tiene etiqueta».
    const rows = form.rows || [];
    const sinCargar = rows.every(isBlankRow);
    const problema = sinCargar ? null : inclusionsError(rows);
    if (sinCargar) {
        falta('inclusions', 'Cargá qué incluye la entrada, o copialo de un evento anterior.');
    } else if (problema) {
        falta('inclusions', `Qué incluye: ${problema}`);
    } else if (!rows.some((row) => row.matchKind !== 'free')) {
        falta('inclusions', 'Qué incluye: hace falta al menos un renglón por categoría o por ítems. Con sólo texto no hay «Sentar a todos» ni reparto del pedido.');
    } else if (Array.isArray(ctx.menu)) {
        // Con el menú a la vista, un código que no está en él es un ítem que salió del menú: el
        // servidor rechaza el renglón entero. Se nombra con lo que guardó el evento copiado,
        // nunca por el código.
        const index = rows.findIndex((row) => itemsNotInMenu(row, ctx.menu).length > 0);
        if (index >= 0) {
            const fuera = itemsNotInMenu(rows[index], ctx.menu);
            const nombres = fuera.map((item) => item.name).filter(Boolean);
            const cuales = nombres.length > 0 ? ` (${nombres.join(', ')})` : '';
            const which = `el renglón ${index + 1} («${texto(rows[index].label)}»)`;
            falta('inclusions', fuera.length === 1
                ? `Qué incluye: ${which} tiene un ítem que ya no está en el menú${cuales}. Quitalo.`
                : `Qué incluye: ${which} tiene ${fuera.length} ítems que ya no están en el menú${cuales}. Quitalos.`);
        }
    }

    // Mesas: sin fecha no hay qué elegir, y eso ya lo dice el campo de la fecha.
    if (conFecha) {
        const disp = ctx.availability || null;
        const tableIds = form.tableIds || [];
        if (ctx.loadingTables) {
            falta('tables', 'Todavía se están buscando las mesas de esa fecha.');
        } else if (!disp) {
            falta('tables', ctx.tablesError ? `No se pudieron consultar las mesas: ${ctx.tablesError}` : 'No se pudieron consultar las mesas de esa fecha.');
        } else if (pasada) {
            // Ya lo dijo el campo de la fecha: con una fecha pasada no hay mesas que elegir.
        } else if (disp.open === false) {
            falta('tables', disp.closedReason || 'El salón no abre a esa hora');
        } else if (tableIds.length === 0) {
            falta('tables', 'Elegí al menos una mesa.');
        } else if (keepFreeTables(disp.tables, tableIds).dropped.length > 0) {
            falta('tables', 'Alguna de las mesas elegidas ya no está libre: destildala.');
        }
    }

    // Quién reserva: obligatorio completo con «Se cobra después»; con «Cobrar ahora», los cinco
    // datos o ninguno (el servidor no acepta una ficha a medias).
    const later = form.paymentMode === 'later';
    if (later || hasCustomerData(form)) {
        const customer = customerOf(form);
        const antes = out.length;
        if (customer.cedula === '') falta('cedula', 'Falta la cédula.');
        else if (!/^\d{6,9}$/.test(customer.cedula)) falta('cedula', 'La cédula va con 6 a 9 dígitos.');
        if (customer.name === '') falta('customerName', 'Falta el nombre.');
        if (customer.lastName === '') falta('customerLastName', 'Falta el apellido.');
        if (customer.phone === '') falta('customerPhone', 'Falta el celular.');
        if (customer.email === '') falta('customerEmail', 'Falta el email.');
        else if (!EMAIL_RE.test(customer.email)) falta('customerEmail', 'El email no parece válido.');
        if (out.length > antes) {
            falta('customer', later
                ? 'Para cobrar después hacen falta los cinco datos: sin ellos no hay a quién cobrarle.'
                : 'Completá los cinco datos o dejalos todos vacíos (vacío va a consumidor final).');
        }
    }

    return out;
};

// De la lista de `validatePrivateEvent` a `{ campo: mensaje }` (el primero de cada campo).
export const errorsByField = (errors) => {
    const out = {};
    (errors || []).forEach((error) => {
        if (out[error.field] === undefined) out[error.field] = error.message;
    });
    return out;
};

// Los bloques de la tarjeta a los que les falta algo, con su rótulo y sin repetir, en el orden
// en que aparecen: «Nombre · Fecha y hora · Mesas».
export const missingBlocks = (errors) => {
    const out = [];
    (errors || []).forEach((error) => {
        const block = FIELD_BLOCK[error.field] || error.field;
        if (!out.includes(block)) out.push(block);
    });
    return out;
};

// ── Lo que viaja ──────────────────────────────────────────────────────────────────────────

// El cuerpo de `POST /manage-events/private`, de un formulario YA validado. `ticketName` no
// viaja: la pantalla no lo pregunta y el servidor pone «Entrada». `notes` viaja sólo si se
// escribió algo. `payment.customer` viaja siempre con «Se cobra después» y, con «Cobrar ahora»,
// sólo si se cargó (sin la clave la venta va a consumidor final).
export const buildPrivateEventBody = (form) => {
    const body = {
        title: texto(form.title),
        branchId: Number(form.branchId),
        startsAt: texto(form.startsAt).slice(0, 16),
        people: entero(form.people),
        price: entero(form.price),
        inclusions: inclusionsPayload(form.rows),
        tableIds: (form.tableIds || []).map(Number),
    };
    const notes = texto(form.notes);
    if (notes !== '') body.notes = notes;

    if (form.paymentMode === 'later') {
        body.payment = { mode: 'later', customer: customerOf(form) };
    } else {
        body.payment = { mode: 'now', method: form.paymentMethod };
        if (hasCustomerData(form)) body.payment.customer = customerOf(form);
    }
    return body;
};

// ── Textos ────────────────────────────────────────────────────────────────────────────────

// «20 × $ 650 = $ 13.000», o null mientras falte alguno de los dos números.
export const totalText = (people, price) => {
    const n = entero(people);
    const p = entero(price);
    // `null >= 0` es true: el precio vacío se descarta aparte.
    if (!(n >= 1) || p === null || !(p >= 0)) return null;
    return `${n} × $ ${convertToMoney(p)} = $ ${convertToMoney(n * p)}`;
};

// El texto de un alta que no salió. El alta es todo o nada, así que cualquier rechazo con código
// del servidor garantiza que no quedó nada creado. Lo que NO lo garantiza es no haber recibido
// respuesta (se cortó la conexión, venció el tiempo) o un error interno (5000): ahí el evento
// puede existir, y como la venta no es idempotente se dice antes de que alguien reintente.
//
// Los rechazos propios de la ruta (7610) llegan como «Evento privado: <detalle>»: el prefijo es
// el nombre de esta pantalla, así que se saca y queda «No se creó nada: <detalle>».
export const rejectionText = (error) => {
    const motivo = (texto(error && error.message) || 'No se pudo completar la operación').replace(/^Evento privado:\s*/i, '');
    const code = Number(error && error.code) || 0;
    if (code === 0 || code === 5000) {
        return `No sabemos si el evento llegó a crearse (${motivo.replace(/[.\s]+$/, '')}). Antes de reintentar, fijate en la lista de Eventos: si ya está, no lo crees de nuevo.`;
    }
    return `No se creó nada: ${motivo}`;
};

// El aviso con el que se llega a «Editar evento» cuando el evento quedó creado y la venta no
// (`saleError` de la respuesta).
export const saleErrorNotice = (saleError) => {
    const motivo = texto(saleError && saleError.message).replace(/[.\s]+$/, '') || 'la venta fue rechazada';
    return `El evento quedó creado con sus mesas, pero las entradas no se registraron: ${motivo}. Vendelas desde Ver asistentes → Venta presencial.`;
};
